import type { EditorialStatus, OperationsReport } from '@buakei/types';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  LoadingState,
  PermissionDeniedState,
} from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/session';
import { STATUS_LABEL } from '@/pages/editorial/status';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { adminApi } from '@/services/api';
import { Page } from '@/shell/Page';

/**
 * Platform activity (M6, ADR-029): counts and costs only, for platform administrators. The
 * server builds the report from aggregates and never includes a person or any text.
 */

const money = (n: number) => `US$${n.toFixed(2)}`;
const count = (n: number | null) => (n === null ? 'Unknown' : n.toLocaleString());
const dayLabel = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-sm text-muted-600">{label}</dt>
      <dd className="font-serif text-2xl font-semibold text-navy-900 tabular-nums">{value}</dd>
      {note ? <dd className="text-xs text-muted-600">{note}</dd> : null}
    </div>
  );
}

function Report({ report }: { report: OperationsReport }) {
  const { ai, content } = report;
  const used = ai.budgetUsd > 0 ? Math.round((ai.committedUsd / ai.budgetUsd) * 100) : 0;
  const tasks: Record<string, string> = {
    research: 'Research questions',
    quick_research: 'Quick research',
    case_brief: 'Case briefs',
    quiz: 'Quizzes',
  };
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle as="h2">AI spend this month</CardTitle>
          <CardDescription>
            Against the application budget for {ai.month}. The budget is enforced by the app; it is
            not a cap on the provider's bill.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Figure label="Spent" value={money(ai.spentUsd)} note="From provider-reported usage" />
            <Figure
              label="Committed"
              value={money(ai.committedUsd)}
              note={`${used}% of ${money(ai.budgetUsd)}, including requests in progress`}
            />
            <Figure
              label="Estimated costs"
              value={String(ai.unreconciled)}
              note="Requests whose cost was estimated; compare with the Anthropic console"
            />
          </dl>
          {used >= 80 ? (
            <p className="mt-4 text-sm text-ink-900">
              <Badge tone="danger">Near the budget</Badge> New questions are refused once the budget
              is reached.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as="h2">Last 14 days</CardTitle>
          <CardDescription>
            {ai.period.requests.toLocaleString()} AI requests · {money(ai.period.costUsd)} ·{' '}
            {ai.period.medianSeconds !== null
              ? `median answer time ${ai.period.medianSeconds}s`
              : 'no answers yet'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl className="grid gap-4 sm:grid-cols-4">
            <Figure label="Answered" value={String(ai.period.completed)} />
            <Figure label="Not found in the library" value={String(ai.period.notFound)} />
            <Figure
              label="Failed"
              value={String(ai.period.failed)}
              note="Busy, unavailable or stopped"
            />
            <Figure
              label="Refused"
              value={String(ai.period.refusedAllowance + ai.period.refusedBudget)}
              note={`${ai.period.refusedAllowance} over the daily limit, ${ai.period.refusedBudget} over budget`}
            />
          </dl>
          <p className="text-sm text-muted-600">
            {Object.entries(ai.period.byTask)
              .map(([task, n]) => `${tasks[task] ?? task}: ${n}`)
              .join(' · ') || 'No requests'}
            {ai.period.open ? ` · ${ai.period.open} running or stopped mid-way` : ''}
          </p>
          <div
            className="overflow-x-auto rounded-md border border-line-200"
            role="region"
            aria-label="Requests by day"
            tabIndex={0}
          >
            <table className="w-full min-w-[32rem] text-left text-sm tabular-nums">
              <caption className="sr-only">AI requests and cost by day, oldest first</caption>
              <thead className="border-b border-line-200 bg-soft-white text-navy-900">
                <tr>
                  <th scope="col" className="p-2 font-semibold">
                    Day
                  </th>
                  <th scope="col" className="p-2 text-right font-semibold">
                    Answered
                  </th>
                  <th scope="col" className="p-2 text-right font-semibold">
                    Failed
                  </th>
                  <th scope="col" className="p-2 text-right font-semibold">
                    Refused
                  </th>
                  <th scope="col" className="p-2 text-right font-semibold">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-200">
                {ai.days.map((d) => (
                  <tr key={d.day}>
                    <th scope="row" className="p-2 font-normal text-ink-900">
                      {dayLabel(d.day)}
                    </th>
                    <td className="p-2 text-right">{d.answered}</td>
                    <td className="p-2 text-right">{d.failed || '—'}</td>
                    <td className="p-2 text-right">{d.refused || '—'}</td>
                    <td className="p-2 text-right">{d.costUsd ? money(d.costUsd) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as="h2">Platform</CardTitle>
          <CardDescription>Totals now.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl className="grid gap-4 sm:grid-cols-3">
            <Figure label="Accounts" value={count(content.accounts)} />
            <Figure label="Acts in the library" value={count(content.acts)} />
            <Figure label="Judgments in the library" value={count(content.cases)} />
            <Figure label="Saved chats" value={count(content.chats)} />
            <Figure label="Research memos" value={count(content.memos)} />
            <Figure label="Open source reports" value={count(content.openReports)} />
          </dl>
          <p className="text-sm text-ink-900">
            Editorial review:{' '}
            {(Object.entries(content.reviews) as [EditorialStatus, number][])
              .map(([state, n]) => `${n} ${STATUS_LABEL[state].label.toLowerCase()}`)
              .join(' · ') || 'no document reviewed yet'}
          </p>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-600">
        Generated {new Date(report.generatedAt).toLocaleString('en-GB')}. Counts and costs only: no
        person, question, chat or memo text is part of this report.
      </p>
    </div>
  );
}

export function ActivityPage() {
  const { session } = useAuth();
  const tokens = useReaderTokens();
  const isAdmin = Boolean(session?.memberships.some((m) => m.roles.includes('platform_admin')));
  const report = useQuery({
    queryKey: ['admin', 'operations'],
    queryFn: () => adminApi.operations(tokens!),
    enabled: Boolean(tokens && isAdmin),
  });
  if (!isAdmin || !tokens) {
    return (
      <Page title="Activity">
        <PermissionDeniedState title="Activity is for platform administrators" />
      </Page>
    );
  }
  return (
    <Page title="Activity" description="Usage and AI spend across the platform." width="wide">
      {report.isPending ? <LoadingState label="Loading activity" /> : null}
      {report.isError ? (
        <ErrorState title="Activity could not be loaded" onRetry={() => void report.refetch()}>
          {report.error.message}
        </ErrorState>
      ) : null}
      {report.data ? <Report report={report.data} /> : null}
    </Page>
  );
}
